// hooks/useConnectionData.js
import { useSelector } from 'react-redux';
import { useMemo, useState, useEffect } from 'react';
import { getFromIndexedDB } from '../Utils/IndexedDBUtils';
import { getAgencyData } from '../Utils/Storage';

const findConn = (connectionData, connID) => {
  if (!connID) return null;
  const numericConnID = parseInt(connID);
  return connectionData.find(conn =>
    conn.ConnID === numericConnID ||
    conn.ConnID === connID ||
    conn.NodeID === numericConnID ||
    conn.NodeID === connID
  ) || null;
};

/**
 * Hook to get connection by ID with automatic updates.
 * `state.connectionData.data` is scoped to the active agency only, so this
 * will return null for connections belonging to another of the user's agencies.
 */
export const useConnectionByID = (connID) => {
  return useSelector(state => findConn(state.connectionData?.data || [], connID));
};

/**
 * Same as useConnectionByID, but falls back to IndexedDB (which stores connections
 * for ALL agencies the user belongs to) when the connection isn't in the active
 * agency's Redux slice. Also reports whether the connection belongs to the
 * currently active agency, so callers can disable actions for foreign-agency data.
 */
export const useConnectionByIDAnyAgency = (connID) => {
  const ownAgencyConnection = useConnectionByID(connID);
  const [fallbackConnection, setFallbackConnection] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (ownAgencyConnection || !connID) {
      setFallbackConnection(null);
      return;
    }
    (async () => {
      try {
        const allConns = await getFromIndexedDB('Conns') || [];
        const found = findConn(allConns, connID);
        if (!cancelled) setFallbackConnection(found);
      } catch (error) {
        console.error('Error loading connection from IndexedDB:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [connID, ownAgencyConnection]);

  const connection = ownAgencyConnection || fallbackConnection;
  const activeAgencyID = getAgencyData()?.AgencyID;
  const isOwnAgencyConnection = !!connection && !!ownAgencyConnection &&
    (activeAgencyID === undefined || activeAgencyID === null ||
      Number(connection.AgencyID || 0) === Number(activeAgencyID));

  return { connection, isOwnAgencyConnection };
};

/**
 * Loads ALL connections from IndexedDB (every agency the user belongs to), unlike
 * `state.connectionData.data` in Redux which is scoped to the active agency only.
 * Needed wherever tasks (which are cross-agency, tied to the user's mobile) are
 * joined against connection data by ConnID - e.g. the Tasks list.
 * Pass a `reloadKey` (e.g. taskData) so it re-reads IDB whenever that value changes.
 */
export const useAllConnectionsFromIDB = (reloadKey) => {
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const allConns = await getFromIndexedDB('Conns') || [];
        if (!cancelled) setConnections(allConns);
      } catch (error) {
        console.error('Error loading all connections from IndexedDB:', error);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  return connections;
};

/**
 * Loads ALL threads from IndexedDB (every agency the user belongs to), unlike
 * `state.threadData.data` in Redux which is scoped to the active agency only.
 * Needed wherever tasks (which are cross-agency, tied to the user's mobile) are
 * joined against thread data by ThreadID - e.g. a task's linked threads.
 * Pass a `reloadKey` (e.g. taskData) so it re-reads IDB whenever that value changes.
 */
export const useAllThreadsFromIDB = (reloadKey) => {
  const [threads, setThreads] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const allThreads = await getFromIndexedDB('Threads') || [];
        if (!cancelled) setThreads(allThreads);
      } catch (error) {
        console.error('Error loading all threads from IndexedDB:', error);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  return threads;
};

/**
 * Hook to get threads by connection ID with automatic updates
 */
export const useThreadsByConnID = (connID) => {
  const connection = useConnectionByID(connID);
  const threadData = useSelector(state => state.threadData?.data) || [];

  return useMemo(() => {
    if (!connID || !connection?.OpenThreadIDs) return [];

    const openThreadIDsArr = connection.OpenThreadIDs
      .split(",")
      .map(id => id.trim())
      .filter(id => id);

    if (openThreadIDsArr.length === 0) return [];
    return threadData.filter(thread =>
      openThreadIDsArr.includes(String(thread.ThreadID))
    );
  }, [connID, connection?.OpenThreadIDs, threadData]);
};

/**
 * Hook to get active threads (filtered)
 */
export const useActiveThreadsByConnID = (connID) => {
  const allThreads = useThreadsByConnID(connID);
  
  return allThreads.filter(thread =>
    thread.StatusID !== 2 && thread.StatusID !== 3
  );
};

/**
 * Hook to get tasks by connection ID
 */
export const useTasksByConnID = (connID) => {
  const connection = useConnectionByID(connID);
  const { data: taskData } = useSelector((state) => state.taskData);

  return useMemo(() => {
    if (!connection?.Tasks) return [];
    try {
      const tasksData = JSON.parse(connection.Tasks);

      return tasksData
        .filter(task => task.Sts !== 0 && task.Sts !== 2)
        .map(task => {
          const matchingTaskData = taskData?.find(td => String(td.TaskID) === String(task.TskID));

          return {
            ...task,
            UID: matchingTaskData?.POCUserID ?? task.UID ?? null,
            CreatedUID: matchingTaskData?.CreatedUserID ?? task.CreatedUID ?? null,
            RmdrDT: matchingTaskData?.RmdrDT ?? task.RmdrDT ?? null,
            CanComplete: (matchingTaskData?.CanComplete ?? task.CanComplete) == true,
            RepeatAfter: Number(matchingTaskData?.RepeatAfter) || 0,
            RepeatDT: matchingTaskData?.RepeatDT || '',
          };
        });
    } catch (error) {
      console.error("Error parsing tasks data:", error);
      return [];
    }
  }, [connection?.Tasks, taskData]); // Added taskData to dependencies
};
/**
 * Hook to get completed tasks by connection ID
 */
export const useCompletedTasksByConnID = (connID) => {
  const connection = useConnectionByID(connID);

  return useMemo(() => {
    if (!connection?.Tasks) return [];
    try {
      const tasksData = JSON.parse(connection.Tasks);
      return tasksData.filter(task => task.Sts === 0 || task.Sts === 2);
    } catch (error) {
      console.error("Error parsing completed tasks data:", error);
      return [];
    }
  }, [connection?.Tasks]);
};

/**
 * Hook to get contacts by connection ID
 */
export const useContactsByConnID = (connID) => {
  const connection = useConnectionByID(connID);

  return useMemo(() => {
    if (!connection) return [];

    const primaryContact = {
      CID: connection.ContactID || connection.ConnID || null,
      CNm: connection.ContactName || "Primary Contact",
      MNo: connection.Mobile || "",
      Eml: connection.Email || "",
      Dsgn: connection.Designation || "",
      CC: connection.CC || "91"
    };

    let additionalContacts = [];
    try {
      if (connection.Contacts) {
        const parsedContacts = typeof connection.Contacts === 'string'
          ? JSON.parse(connection.Contacts)
          : connection.Contacts;
        additionalContacts = Array.isArray(parsedContacts) ? parsedContacts : [];
      }
    } catch (error) {
      console.error("Error parsing contacts data:", error);
    }

    return [primaryContact, ...additionalContacts];
  }, [connection?.ContactName, connection?.Mobile, connection?.Email, connection?.Designation, connection?.Contacts, connection?.ContactID, connection?.ConnID]);
};